"use client"

import { ClientOnly } from "@/lib/animation/client-only"
import { Wizard } from "@/components/quiz/wizard"
import type { PriceMap } from "@/lib/pricing"
import type { InsurersData } from "@/lib/quiz/insurers"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import type { QuizMarket } from "@/lib/quiz/market"

interface Props {
  prices: PriceMap
  productIds: PriceMap
  insurers: InsurersData
  experts: ReviewerExpert[]
  market: QuizMarket
  /**
   * "default" — embedded page, max-w-3xl with normal vertical padding.
   * "focus"   — Hims-style focused mode: narrower column, generous
   *             top/bottom padding so the question floats vertically.
   *             Used by /quiz-focus inside the minimal-chrome layout.
   */
  variant?: "default" | "focus"
}

export function QuizClient({
  prices,
  productIds,
  insurers,
  experts,
  market,
  variant = "default",
}: Props) {
  if (variant === "focus") {
    // No `justify-center` here. Top-padding pins the kicker (and
    // therefore the title block that follows) at a stable offset
    // from the chrome bottom, so the kicker doesn't jump upward when
    // the title wraps to 2 or 3 lines. Content flows downward from
    // that anchor.
    return (
      <section className="mx-auto flex min-h-[calc(100svh-8rem)] w-full max-w-[640px] flex-col px-6 pt-[18vh] pb-16">
        <ClientOnly fallback={null}>
          <Wizard
            prices={prices}
            productIds={productIds}
            insurers={insurers}
            experts={experts}
            market={market}
            variant="focus"
          />
        </ClientOnly>
      </section>
    )
  }

  return (
    <section className="mx-auto max-w-3xl px-6 py-12 md:py-16">
      <ClientOnly fallback={null}>
        <Wizard
          prices={prices}
          productIds={productIds}
          insurers={insurers}
          experts={experts}
          market={market}
        />
      </ClientOnly>
    </section>
  )
}
